import { useState } from "react";
import { FiCheckCircle, FiChevronDown } from "react-icons/fi";
import { unpricedReasonLabel } from "../../../../components/usageBilling/usageFormat";

// Calls the backend couldn't price in the selected cycle, grouped by reason.
// They add $0 to spend rather than a guess, so every total above is low by
// these calls. The backend sends one row per service:operation:model:reason;
// each reason expands to those rows.
export default function UnpricedCallsTable({ rows, language }) {
  const zh = language === "zh";
  const [open, setOpen] = useState(null);

  const byReason = [];
  for (const row of rows) {
    let entry = byReason.find((r) => r.reason === row.reason);
    if (!entry) {
      entry = { reason: row.reason, calls: 0, rows: [] };
      byReason.push(entry);
    }
    entry.calls += row.calls;
    entry.rows.push(row);
  }
  byReason.sort((a, b) => b.calls - a.calls);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">
          {zh ? "未計價的呼叫" : "Unpriced calls"}
        </h3>
        <p className="text-sm text-gray-500">
          {zh
            ? "系統無法計價的模型呼叫。它們以 $0 計入，所以上面的花費會比實際成本低。"
            : "Model calls the system couldn't price. They count as $0, so the spend above is lower than the real cost by these calls."}
        </p>
      </div>

      {byReason.length === 0 ? (
        <p className="flex items-center gap-2 px-5 py-6 text-sm text-green-700">
          <FiCheckCircle className="w-4 h-4" aria-hidden="true" />
          {zh
            ? "本週期沒有未計價的呼叫。"
            : "No unpriced calls in this cycle."}
        </p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {byReason.map((entry) => {
            const expanded = open === entry.reason;
            return (
              <li key={entry.reason}>
                <button
                  type="button"
                  onClick={() => setOpen(expanded ? null : entry.reason)}
                  aria-expanded={expanded}
                  className="w-full flex items-center justify-between gap-3 px-5 py-3 text-left hover:bg-gray-50"
                >
                  <span>
                    <span className="block text-sm font-medium text-gray-900">
                      {unpricedReasonLabel(entry.reason, language)}
                    </span>
                    <span className="block text-xs font-mono text-gray-500">
                      {entry.reason}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-gray-900 tabular-nums">
                      {entry.calls}{" "}
                      <span className="font-normal text-gray-500">
                        {zh ? "次" : entry.calls === 1 ? "call" : "calls"}
                      </span>
                    </span>
                    <FiChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        expanded ? "rotate-180" : ""
                      }`}
                      aria-hidden="true"
                    />
                  </span>
                </button>
                {expanded && (
                  <table className="w-full text-sm bg-gray-50">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="px-5 py-2 text-left font-medium">
                          {zh ? "服務" : "Service"}
                        </th>
                        <th className="px-5 py-2 text-left font-medium">
                          {zh ? "操作" : "Operation"}
                        </th>
                        <th className="px-5 py-2 text-left font-medium">
                          {zh ? "模型" : "Model"}
                        </th>
                        <th className="px-5 py-2 text-right font-medium">
                          {zh ? "次數" : "Calls"}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 font-mono text-xs text-gray-700">
                      {entry.rows.map((row) => (
                        <tr
                          key={`${row.source_service}:${row.operation}:${row.model}`}
                        >
                          <td className="px-5 py-2">{row.source_service || "—"}</td>
                          <td className="px-5 py-2">{row.operation || "—"}</td>
                          <td className="px-5 py-2">{row.model || "—"}</td>
                          <td className="px-5 py-2 text-right tabular-nums">
                            {row.calls}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
